import { collection, query, getDocs, limit } from "firebase/firestore";
import { projectFirestore } from "@/firebase/config";
import { storeLoginLog } from "@/composables/useLoginLogs";

// Get device information
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const language = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const screenResolution = `${screen.width}x${screen.height}`;
  const colorDepth = screen.colorDepth;
  const cookieEnabled = navigator.cookieEnabled;
  const onlineStatus = navigator.onLine;

  // Get browser name
  let browserName = "Unknown";
  if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
    browserName = "Chrome";
  } else if (userAgent.includes("Firefox")) {
    browserName = "Firefox";
  } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
    browserName = "Safari";
  } else if (userAgent.includes("Edg")) {
    browserName = "Edge";
  } else if (userAgent.includes("Opera")) {
    browserName = "Opera";
  }

  // Get OS name
  let osName = "Unknown";
  if (platform.includes("Win")) {
    osName = "Windows";
  } else if (platform.includes("Mac")) {
    osName = "macOS";
  } else if (platform.includes("Linux")) {
    osName = "Linux";
  } else if (userAgent.includes("Android")) {
    osName = "Android";
  } else if (userAgent.includes("iOS")) {
    osName = "iOS";
  }

  return {
    browser: browserName,
    os: osName,
    platform,
    language,
    timezone,
    screenResolution,
    colorDepth,
    cookieEnabled,
    onlineStatus,
    userAgent: userAgent.substring(0, 100) + "...", // Truncate for readability
  };
};

// Get IP address (approximate location)
const getIPInfo = async () => {
  try {
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();
    return {
      ip: data.ip || "Unknown",
      city: data.city || "Unknown",
      region: data.region || "Unknown",
      country: data.country_name || "Unknown",
      isp: data.org || "Unknown",
    };
  } catch (error) {
    console.error("Error fetching IP info:", error);
    return {
      ip: "Unknown",
      city: "Unknown",
      region: "Unknown",
      country: "Unknown",
      isp: "Unknown",
    };
  }
};

// Get system settings from Firestore
const getSystemSettings = async () => {
  try {
    const settingsCollection = collection(projectFirestore, "settings");
    const q = query(settingsCollection, limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const settingsDoc = querySnapshot.docs[0];
      const data = settingsDoc.data();
      return {
        telegramToken: data.telegramToken || "",
        chatIds: data.chatIds || [],
      };
    }
    return null;
  } catch (error) {
    console.error("Error loading settings:", error);
    return null;
  }
};

// Send message to Telegram
const sendTelegramMessage = async (message) => {
  try {
    const settings = await getSystemSettings();

    if (!settings || !settings.telegramToken || settings.chatIds.length === 0) {
      console.warn("Telegram settings not configured");
      return { success: false, error: "Telegram settings not configured" };
    }

    const { telegramToken, chatIds } = settings;
    const promises = chatIds.map(async (chatId) => {
      const url = `https://api.telegram.org/bot${telegramToken}/sendMessage`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chat_id: chatId,
          text: message,
          parse_mode: "HTML",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    });

    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error("Error sending Telegram message:", error);
    return { success: false, error: error.message };
  }
};

// Format login notification message
const formatLoginMessage = (userInfo, deviceInfo, ipInfo) => {
  const timestamp = new Date().toLocaleString();

  return `
🔐 <b>New Login Alert</b>

👤 <b>User Information:</b>
• Email: ${userInfo.email || "Unknown"}
• UID: ${userInfo.uid || "Unknown"}
• Display Name: ${userInfo.displayName || "Not set"}

🖥️ <b>Device Information:</b>
• Browser: ${deviceInfo.browser}
• OS: ${deviceInfo.os}
• Platform: ${deviceInfo.platform}
• Language: ${deviceInfo.language}
• Timezone: ${deviceInfo.timezone}
• Screen: ${deviceInfo.screenResolution}
• Color Depth: ${deviceInfo.colorDepth}-bit
• Cookies: ${deviceInfo.cookieEnabled ? "Enabled" : "Disabled"}
• Online: ${deviceInfo.onlineStatus ? "Yes" : "No"}

🌍 <b>Location Information:</b>
• IP Address: ${ipInfo.ip}
• Location: ${ipInfo.city}, ${ipInfo.region}, ${ipInfo.country}
• ISP: ${ipInfo.isp}

⏰ <b>Login Time:</b> ${timestamp}

🔗 <b>User Agent:</b>
${deviceInfo.userAgent}
  `.trim();
};

// Send login notification
const sendLoginNotification = async (userInfo) => {
  try {
    const deviceInfo = getDeviceInfo();
    const ipInfo = await getIPInfo();

    // Store login log in Firestore
    const logResult = await storeLoginLog(userInfo, deviceInfo, ipInfo);
    if (!logResult.success) {
      console.warn("Failed to store login log:", logResult.error);
    }

    // Send Telegram notification
    const message = formatLoginMessage(userInfo, deviceInfo, ipInfo);
    return await sendTelegramMessage(message);
  } catch (error) {
    console.error("Error sending login notification:", error);
    return { success: false, error: error.message };
  }
};

export {
  sendTelegramMessage,
  sendLoginNotification,
  getDeviceInfo,
  getIPInfo,
  getSystemSettings,
};
